// FE mirror of server lock tier predicate.
// Server source of truth: server/src/routes/invoices.js getLockTier().
// Vocabulary asymmetries (e.g. column billitSubmittedAt's semantic
// shift in v2.1) are documented in docs/architecture/vocabulary_drift.md.
//
// Phase 35 Stage-3 r2 — the lock tier keys PURELY off the send channels (and the
// legacy push flag), NEVER off payment status. A pro forma reads "unpaid" from
// the moment it is created, so keying the lock on paymentStatus locked every
// active PF (the smoke-test blocker: the footer collapsed to "Cancel" and
// Edit / Send / Delete vanished). Only a SENT pro forma should lock.
//   - push-locked: billitSubmittedAt non-null (legacy Billit push). Editable:
//     notes, billitOrderId.
//   - sent-locked: sentToClientAt and/or sentToPeppolAt set. Editable: notes.
//   - none: not sent. All fields editable. Payment status is irrelevant here.

import type { Invoice } from "@shared/entities";
import { tImperative as _tImp } from "../i18n";

// 179 C.2 — `POST_SEND_STATUSES` STOOD HERE, retained only for the deprecated
// LOCKED_STATUSES alias that is gone with it: getLockTier never consulted
// payment status ("sent" is the two channel stamps).
export const ALLOWED_POST_SEND_FIELDS = ["notes"];
// 179 C.3 — `ALLOWED_POST_PUSH_FIELDS` STOOD HERE: the push-locked tier went
// with the push (no live path ever stamped billitSubmittedAt).

// 160 §1 — "SENT" ON ITS OWN, because a caller needed it on its own and the
// tier is not it. `server/src/routes/invoices.js:58` is the definition being
// mirrored: EITHER channel alone puts the pro forma in stage 2. The tier below
// answers a wider question (it also reports the legacy Billit push), so
// spending it as a sent-test would refuse a payment removal the server allows
// — the approximation rule 4 forbids. One predicate, two readers.
export function pfIsSent(invoice: Invoice | null | undefined) {
  return !!(invoice?.sentToClientAt || invoice?.sentToPeppolAt);
}

export function getLockTier(invoice: Invoice | null | undefined) {
  if (pfIsSent(invoice)) return "sent-locked";
  return "none";
}

export function getAllowedFields(invoice: Invoice | null | undefined) {
  const tier = getLockTier(invoice);
  if (tier === "sent-locked") return ALLOWED_POST_SEND_FIELDS;
  return null;
}

export function isFieldEditable(
  invoice: Invoice | null | undefined,
  fieldName: string,
) {
  const allowed = getAllowedFields(invoice);
  return allowed === null || allowed.includes(fieldName);
}

// brief 40 §5 — the two label sentences live in the dictionary; the old
// literals ride as fallbacks so an unseeded box renders what it always did.
export function getLockTierLabel(tier: string) {
  if (tier === "sent-locked")
    return _tImp("proforma.lock.sent_locked", {
      fallback: "Locked: pro forma has been sent to the client",
    });
  return null;
}

// 179 C.2 — the "DEPRECATED, remove in a later sweep" block STOOD HERE:
// LOCKED_STATUSES, POST_SUBMISSION_ALLOW_LIST, isLocked (one importer, which
// never called it) and isFieldEditableWhenLocked, none with a live reader.
// This is that later sweep.
